"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ExclusiveCollectionBanner() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const stats = [{
    label: "NEW ARRIVALS",
    value: "24+"
  }, {
    label: "STARTING",
    value: "$89 USD"
  }, {
    label: "SHIPPING",
    value: "WORLDWIDE"
  }];
  return <section data-section-id="4241" id="exclusivecollectionbanner" className="relative bg-[var(--black)] text-white w-full min-h-[100vh] lg:h-screen overflow-hidden">
      {}
      <div className="absolute inset-0 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_runway_showcase.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_runway_showcase.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_runway_showcase.webp-wvc-sizes" alt="Model walking on runway wearing premium seasonal fashion collection" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover animate-[vantageZoom_8s_ease-out_forwards] scale-105 grayscale contrast-125 opacity-40" />
        {}
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
        backgroundImage: `radial-gradient(var(--accent) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />
        {}
        <div className="absolute inset-0" style={{
        background: "linear-gradient(90deg, hsl(0 0% 7% / 0.95) 0%, hsl(0 0% 7% / 0.85) 30%, hsl(0 0% 7% / 0.55) 55%, hsl(0 0% 7% / 0.2) 80%, hsl(0 0% 7% / 0.3) 100%)"
      }} />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, hsl(0 0% 7% / 0.3) 0%, hsl(0 0% 7% / 0) 40%, hsl(0 0% 7% / 0) 70%, hsl(0 0% 7% / 0.5) 100%)"
      }} />
      </div>

      <style>{`
        @keyframes vantageZoom {
          0% { transform: scale(1.12); }
          100% { transform: scale(1.02); }
        }
      `}</style>

      {}
      <div className="relative z-10 h-full min-h-[100vh] lg:min-h-0 lg:h-screen flex flex-col">
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-0">
            <div ref={contentRef} className={`max-w-3xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              {}
              <div className="flex items-center gap-3 mb-8 lg:mb-12">
                <span className="h-px w-8 bg-[var(--accent)]" />
                <span className="font-default text-xs sm:text-sm tracking-[0.25em] uppercase text-[var(--accent)]">
                  Lush Fashion Seasonal
                </span>
              </div>

              {}
              <h2 className="font-heading font-bold leading-[0.95] tracking-tighter text-white text-[14vw] sm:text-[10vw] md:text-[8vw] lg:text-[7.5vw] xl:text-[120px]">
                <span className="block">ELEVATED</span>
                <span className="block text-[var(--accent)]">STYLE</span>
                <span className="block">DESIGNED</span>
                <span className="block font-serif italic font-light">for you.</span>
              </h2>

              {}
              <p className="font-default text-base sm:text-lg lg:text-[18px] leading-relaxed text-white/80 mt-8 lg:mt-12 max-w-xl">
                Discover our latest collection of premium apparel, meticulously crafted for the modern wardrobe. From timeless silhouettes to contemporary textures, every piece is designed to define your unique aesthetic. Experience the intersection of luxury and comfort.
              </p>

              {}
              <div className="flex flex-col sm:flex-row gap-6 mt-10 lg:mt-12">
                <Button asChild size="lg" className="h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-xs tracking-[0.2em] uppercase border border-[var(--accent)] rounded-lg">
                  <Link to="/ShopPage">Shop New Arrivals</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-14 px-8 bg-transparent border border-white text-white hover:bg-white hover:text-[var(--black)] font-default text-xs tracking-[0.2em] uppercase rounded-lg">
                  <Link to="/SeasonalCollectionsPage">
                    View Collections
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="relative px-6 sm:px-10 lg:px-16 pb-10 lg:pb-12 flex justify-start lg:justify-end">
          <div ref={statsRef} className={`backdrop-blur-xl bg-white/5 border border-white/10 shadow-lg px-6 py-5 sm:px-8 sm:py-6 transition-all duration-1000 ease-out delay-300 rounded-lg ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          boxShadow: "0px 30px 80px -20px hsl(45 68% 20% / 0.2), inset 0 1px 0 0 hsl(0 0% 100% / 0.05)"
        }}>
            <div className="flex items-center gap-6 sm:gap-10">
              {stats.map((stat, i) => <div data-index={i} key={stat.label} className="flex flex-col gap-1.5 relative">
                  {i > 0 && <span className="absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 h-8 w-px bg-white/20" />}
                  <span className="font-default text-[10px] tracking-[0.2em] uppercase text-white/60">
                    {stat.label}
                  </span>
                  <span className="font-default font-bold text-base sm:text-lg text-white tracking-tight">
                    {stat.value}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}